import { useUserStore } from "../store/user-store"
import { usePostStore, markInteractionMutated } from "../store/post-store"
import { useFeedStore } from "../store/feed-store"
import {
  followUser as apiFollowUser,
  unfollowUser as apiUnfollowUser,
  togglePostLike as apiTogglePostLike,
  togglePostFavorite as apiTogglePostFavorite,
  publishPost as apiPublishPost,
  updatePost as apiUpdatePost,
  deletePost as apiDeletePost,
  copyAbilityToAccount as apiCopyAbility,
  deployTeam as apiDeployTeam,
} from "../community-api"
import { normalizePost } from "../normalize"
import { invalidateCache } from "./request-dedup"
import type { PublishDraft, CommunityPost } from "../types"

export async function toggleFollow(userId: string): Promise<boolean> {
  const user = useUserStore.getState().entities[userId]
  if (!user) return false
  const wasFollowed = user.isFollowed ?? false
  const prevFollowers = user.stats.followers

  // Optimistic update
  useUserStore.getState().updateUser(userId, {
    isFollowed: !wasFollowed,
    stats: { ...user.stats, followers: prevFollowers + (wasFollowed ? -1 : 1) },
  })

  try {
    if (wasFollowed) await apiUnfollowUser(userId)
    else await apiFollowUser(userId)
    invalidateCache(`user:${userId}`)
    return !wasFollowed // 返回关注后的状态
  } catch (err) {
    // Delta-based rollback
    const current = useUserStore.getState().entities[userId]
    useUserStore.getState().updateUser(userId, {
      isFollowed: wasFollowed,
      stats: {
        ...(current?.stats ?? user.stats),
        followers: (current?.stats.followers ?? prevFollowers) + (wasFollowed ? 1 : -1),
      },
    })
    throw err // 重新抛出，让组件层处理 toast
  }
}

export async function togglePostLike(postId: string): Promise<void> {
  const post = usePostStore.getState().entities[postId]
  if (!post) return
  const wasLiked = post.isLiked
  const prevLikes = post.stats.likes
  const delta = wasLiked ? -1 : 1

  usePostStore.getState().updatePost(postId, {
    isLiked: !wasLiked,
    stats: { ...post.stats, likes: prevLikes + delta },
  })
  markInteractionMutated(postId)

  try {
    const result = await apiTogglePostLike(postId, wasLiked)
    // Server returns the authoritative delta; reconcile count
    const correctedLikes = prevLikes + result.count
    usePostStore.getState().updatePost(postId, {
      isLiked: result.liked,
      stats: { ...(usePostStore.getState().entities[postId]?.stats ?? post.stats), likes: Math.max(0, correctedLikes) },
    })
    invalidateCache(`post:${postId}`)
  } catch (err) {
    const current = usePostStore.getState().entities[postId]
    usePostStore.getState().updatePost(postId, {
      isLiked: wasLiked,
      stats: {
        ...(current?.stats ?? post.stats),
        likes: Math.max(0, (current?.stats.likes ?? prevLikes) - delta),
      },
    })
    throw err
  }
}

export async function togglePostFavorite(postId: string): Promise<void> {
  const post = usePostStore.getState().entities[postId]
  if (!post) return
  const wasFavorited = post.isFavorited
  const prevFavorites = post.stats.favorites
  const delta = wasFavorited ? -1 : 1

  usePostStore.getState().updatePost(postId, {
    isFavorited: !wasFavorited,
    stats: { ...post.stats, favorites: Math.max(0, prevFavorites + delta) },
  })
  markInteractionMutated(postId)

  try {
    const result = await apiTogglePostFavorite(postId, wasFavorited)
    usePostStore.getState().updatePost(postId, { isFavorited: result.favorited })
    invalidateCache(`post:${postId}`)
  } catch (err) {
    const current = usePostStore.getState().entities[postId]
    usePostStore.getState().updatePost(postId, {
      isFavorited: wasFavorited,
      stats: {
        ...(current?.stats ?? post.stats),
        favorites: Math.max(0, (current?.stats.favorites ?? prevFavorites) - delta),
      },
    })
    throw err
  }
}

export async function copyAbilityToAccount(postId: string): Promise<void> {
  await apiCopyAbility(postId)
  const post = usePostStore.getState().entities[postId]
  if (post) {
    usePostStore.getState().updatePost(postId, {
      isJoined: true,
      stats: { ...post.stats, copies: post.stats.copies + 1 },
    })
  }
}

export async function deployTeam(postId: string): Promise<void> {
  await apiDeployTeam(postId)
  const post = usePostStore.getState().entities[postId]
  if (post) {
    usePostStore.getState().updatePost(postId, {
      isJoined: true,
      stats: { ...post.stats, copies: post.stats.copies + 1 },
    })
  }
}

export async function publishPost(draft: PublishDraft): Promise<CommunityPost> {
  const post = await apiPublishPost(draft)
  const { post: normalized, author } = normalizePost(post)

  usePostStore.getState().upsertPost(normalized)
  useUserStore.getState().upsertUser(author)

  useFeedStore.getState().invalidate("recommended")
  useFeedStore.getState().invalidate("latest")
  useFeedStore.getState().invalidateMatching(`user=${author.id}`)

  return post
}

export async function editPost(postId: string, updates: Parameters<typeof apiUpdatePost>[1]): Promise<void> {
  await apiUpdatePost(postId, updates)
  invalidateCache(`post:${postId}`)
}

export async function removePost(postId: string): Promise<void> {
  const post = usePostStore.getState().entities[postId]
  await apiDeletePost(postId)
  usePostStore.getState().removePost(postId)
  if (post) {
    useFeedStore.getState().invalidateMatching(`user=${post.authorId}`)
  }
  useFeedStore.getState().invalidate("recommended")
  useFeedStore.getState().invalidate("latest")
}
